import './Drawer.scss';
import { PropsWithChildren, useEffect, useState } from 'react';
import { isTelegramWebview } from '../../data/device';
import { assets } from '../../assets/assets';
import { useAppUpdates, app } from '../../data/AppController';
import { Animated } from '../shared/Animated/Animated';
import { DrawerPowerUpInfo } from './drawers/DrawerPowerUpInfo';
import { DrawerPowerUpGiftExpired } from './drawers/DrawerPowerUpGiftExpired';
import { DrawerPowerUpEarn } from './drawers/DrawerPowerUpEarn';
import { DrawerGeneric } from './drawers/DrawerGeneric';
import { DrawerPowerUpCardPromo } from './drawers/DrawerPowerUpCardPromo';
import { DrawerPowerUpCardGiftReceived } from './drawers/DrawerPowerUpCardGiftReceived';
import { DrawerCriticalError } from './drawers/DrawerCriticalError';
import { DrawerDailyAirdrop } from './drawers/DrawerDailyAirdrop';
import { DrawerDailyReward } from './drawers/DrawerDailyReward';
import { DrawerEarnCheck } from './drawers/DrawerEarnCheck';
import { DrawerInviteFriend } from './drawers/DrawerInviteFriend';
import { DrawerLeaderboard } from './drawers/DrawerLeaderboard';
import { DrawerOutOfEnergy } from './drawers/DrawerOutOfEnergy';
import { DrawerRewardsSummary } from './drawers/DrawerRewardsSummary';
import { DrawerWelcome } from './drawers/DrawerWelcome';
import { DrawerPowerUp } from './drawers/DrawerPowerUp/DrawerPowerUp';
import { DrawerFriendsDetails } from './drawers/DrawerFriendsDetails';
import { CreateTeamDetails } from '../pages/TeamPages/CreateTeamDetails';
import { DrawerMultipleGifts } from './drawers/DrawerMultipleGifts';
import { DrawerQuestCheck } from '../pages/EarnPage/DrawerQuestCheck/DrawerQuestCheck';

// import { DrawerOffchainToken } from '../pages/TradingPage/TradingTokenPage/TradingTokenPage';
// import { DrawerTradingCreate } from '../pages/TradingCreatePage/TradingCreatePage';
import { DrawerTradingCreateConfirm } from '../pages/TradingCreatePage/DrawerTradingCreateConfirm/DrawerTradingCreateConfirm';
import { DrawerTradingTransactionSuccess } from '../pages/TradingTokenPage/DrawerTradingTransaction/DrawerTradingTransactionSuccess';
import { DrawerTradingTransactionError } from '../pages/TradingTokenPage/DrawerTradingTransaction/DrawerTradingTransactionError';
import { DrawerTradingComingsoon } from '../pages/TradingTokenPage/DrawerTradingComingSoon/DrawerTradingComingsoon';
import { DrawerTradingTransactionConfirm } from '../pages/TradingTokenPage/DrawerTradingTransactionConfirm/DrawerTradingTransactionConfirm';
import { DrawerTradingSlippage } from '../pages/TradingTokenPage/DrawerTradingSlippage/DrawerTradingSlippage';
import { DrawerTradingCreationSuccess } from '../pages/TradingTokenPage/DrawerTradingTransaction/DrawerTradingCreationSuccess';
import { DrawerS1 } from '../pages/TradingTokenPage/DrawerS1/DrawerS1';
import { DrawerQuestPromo } from './drawers/DrawerQuestPromo';
import { DrawerFollow } from './drawers/DrawerFollow';
import { DrawerTradingWarning } from '../pages/TradingTokenPage/DrawerTradingWarning/DrawerTradingWarning';

const drawers = {
  generic: DrawerGeneric,
  criticalError: DrawerCriticalError,
  dailyAirdrop: DrawerDailyAirdrop,
  dailyReward: DrawerDailyReward,
  /**
   * @deprecated Tutorial needs to be migrated to use questCheck, for now use `earnChecker` for the tutorial
   */
  earnChecker: DrawerEarnCheck,
  inviteFriend: DrawerInviteFriend,
  leaderboard: DrawerLeaderboard,
  outOfEnergy: DrawerOutOfEnergy,
  rewardSummary: DrawerRewardsSummary,
  welcome: DrawerWelcome,
  minePromo: DrawerPowerUpCardPromo,
  mineGiftReceived: DrawerPowerUpCardGiftReceived,
  mineGiftExpired: DrawerPowerUpGiftExpired,
  mineCardReward: DrawerPowerUpEarn,
  mineBonusInfo: DrawerPowerUpInfo,
  mineCardDetails: DrawerPowerUp,
  friendsDetails: DrawerFriendsDetails,
  createTeam: CreateTeamDetails,
  multipleGifts: DrawerMultipleGifts,
  questCheck: DrawerQuestCheck,
  // questPromo: DrawerQuestPromo,
  follow: DrawerFollow,

  drawerTradingCreateConfirm: DrawerTradingCreateConfirm,
  drawerTradingTransactionConfirm: DrawerTradingTransactionConfirm,
  drawerTradingCreationSuccess: DrawerTradingCreationSuccess,
  drawerTradingTransactionSuccess: DrawerTradingTransactionSuccess,
  drawerTradingTransactionError: DrawerTradingTransactionError,
  drawerTradingComingSoon: DrawerTradingComingsoon,
  drawerTradingSlippage: DrawerTradingSlippage,
  drawerTradingWarning: DrawerTradingWarning,

  drawerS1: DrawerS1,
};
export type Drawers = keyof typeof drawers;

export const Drawers = () => {
  useAppUpdates({
    id: 'Drawer',
    listener: app.ui.drawer.view.attachEventListener(),
  });

  const { visible, data } = app.ui.drawer.view;

  const onClose = () => {
    app.ui.drawer.close();
  };

  const Component = drawers[data?.id || 'generic'];

  return (
    <Animated
      visible={visible}
      anim={{
        in: 'slide-up',
        out: 'slide-down',
      }}
    >
      <div className={`modal-drawer ${data?.id}`} style={{ opacity: 1 }}>
        {data?.backdropClose && (
          <div className="backdrop-hitbox" onClick={onClose}></div>
        )}
        <div
          className="modal-drawer-box"
          style={{
            paddingBottom: isTelegramWebview() ? '36px' : '24px',
            zIndex: 2,
          }}
        >
          {!data?.hideClose && (
            <img
              className="modal-drawer-button-close"
              src={assets.button_x}
              onClick={onClose}
            />
          )}
          <Component />
        </div>
      </div>
    </Animated>
  );
};

interface Props extends PropsWithChildren {
  className: string;
  visible: boolean;
  onClose: () => void;
}

export const Drawer = ({ children, className, visible, onClose }: Props) => {
  const [showedOnce, setShowedOnce] = useState(false);

  useEffect(() => {
    if (visible) {
      setShowedOnce(true);
    }
  }, [visible]);

  return (
    <div
      className={`modal-drawer ${className} ${
        visible ? 'slide-up' : 'slide-down'
      } ${!showedOnce && 'hide'}`}
    >
      <div
        className="modal-drawer-box"
        style={{ paddingBottom: isTelegramWebview() ? '36px' : '24px' }}
      >
        <img
          className="modal-drawer-button-close"
          src={assets.button_x}
          onClick={onClose}
        />

        {children}
      </div>
    </div>
  );
};
